/**
 * AdminActivityLogFeed — real-time URL activity feed for Sub-Admin & Super-Admin.
 *
 * Renders incoming URL navigation, layout hydration, dual-key, and copilot
 * access events as an auto-refreshing scrollable table.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Activity, RefreshCw, Filter, ChevronDown } from "lucide-react";

const EVENT_COLORS = {
    NAVIGATION_DEEP_LINK: "bg-blue-500/10 text-blue-500 border-blue-500/30",
    LAYOUT_SHARE_HYDRATE: "bg-violet-500/10 text-violet-500 border-violet-500/30",
    DUAL_KEY_TRIGGER:     "bg-amber-500/10 text-amber-500 border-amber-500/30",
    COPILOT_TIER_ACCESS:  "bg-emerald-500/10 text-emerald-500 border-emerald-500/30",
};

const EVENT_LABELS = {
    NAVIGATION_DEEP_LINK: "Deep Link",
    LAYOUT_SHARE_HYDRATE: "Layout Hydrate",
    DUAL_KEY_TRIGGER:     "Dual Key",
    COPILOT_TIER_ACCESS:  "Copilot Access",
};

function relativeTime(iso) {
    if (!iso) return "";
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 60000) return `${Math.round(diff / 1000)}s ago`;
    if (diff < 3600000) return `${Math.round(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.round(diff / 3600000)}h ago`;
    return `${Math.round(diff / 86400000)}d ago`;
}

export default function AdminActivityLogFeed({ autoRefreshMs = 15000, maxItems = 50 }) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");
    const [showFilter, setShowFilter] = useState(false);

    const fetchEvents = useCallback(async () => {
        try {
            const params = { last: maxItems };
            const res = await api.get("/admin/audit/url-events/stream", { params });
            setEvents(res.data?.events || []);
        } catch {
            // Silent — admin feed is non-critical
        } finally {
            setLoading(false);
        }
    }, [maxItems]);

    // Initial fetch + auto-refresh interval
    useEffect(() => {
        fetchEvents();
        const interval = setInterval(fetchEvents, autoRefreshMs);
        return () => clearInterval(interval);
    }, [fetchEvents, autoRefreshMs]);

    const filtered = filter
        ? events.filter((e) => e.event_type === filter)
        : events;

    return (
        <Card data-testid="admin-url-activity-feed">
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                    <Activity className="w-5 h-5 text-primary" />
                    URL Activity Feed
                </CardTitle>
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowFilter((v) => !v)}
                        data-testid="url-feed-filter-toggle"
                    >
                        <Filter className="w-3.5 h-3.5 mr-1" />
                        Filter
                        <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${showFilter ? "rotate-180" : ""}`} />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={fetchEvents}
                        disabled={loading}
                        data-testid="url-feed-refresh"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                    </Button>
                </div>
            </CardHeader>

            {showFilter && (
                <div className="px-6 pb-2 flex flex-wrap gap-1.5">
                    <button
                        onClick={() => setFilter("")}
                        className={`text-[10px] px-2 py-0.5 rounded-full border ${!filter ? "bg-primary text-primary-foreground" : "bg-secondary"}`}
                        data-testid="url-feed-filter-all"
                    >
                        All
                    </button>
                    {Object.entries(EVENT_LABELS).map(([key, label]) => (
                        <button
                            key={key}
                            onClick={() => setFilter(key)}
                            className={`text-[10px] px-2 py-0.5 rounded-full border ${filter === key ? "bg-primary text-primary-foreground" : "bg-secondary"}`}
                            data-testid={`url-feed-filter-${key}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            )}

            <CardContent>
                {loading && events.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-4">Loading events...</div>
                ) : filtered.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-4">No events in this window.</div>
                ) : (
                    <div className="max-h-[400px] overflow-y-auto space-y-1.5">
                        {filtered.map((evt, i) => (
                            <div
                                key={`${evt.timestamp}-${i}`}
                                className="flex items-start gap-3 p-2 rounded-lg border border-border/30 hover:bg-secondary/30 transition-colors text-xs"
                                data-testid={`url-event-row-${i}`}
                            >
                                <Badge
                                    variant="outline"
                                    className={`shrink-0 text-[9px] px-1.5 py-0 ${EVENT_COLORS[evt.event_type] || "bg-muted"}`}
                                >
                                    {EVENT_LABELS[evt.event_type] || evt.event_type}
                                </Badge>
                                <div className="flex-1 min-w-0">
                                    <div className="font-mono text-[11px] truncate" title={evt.canonical_path}>
                                        {evt.canonical_path || "—"}
                                    </div>
                                    {evt.query_params && Object.keys(evt.query_params).length > 0 && (
                                        <div className="text-muted-foreground text-[10px] truncate">
                                            ?{Object.entries(evt.query_params).map(([k, v]) => `${k}=${v}`).join("&")}
                                        </div>
                                    )}
                                </div>
                                <div className="shrink-0 text-right text-muted-foreground">
                                    <div className="text-[10px]">{evt.user_email || evt.user_id?.slice(0, 8) || "—"}</div>
                                    <div className="text-[9px]">{relativeTime(evt.timestamp)}</div>
                                    <Badge variant="outline" className="text-[8px] px-1 py-0 mt-0.5">
                                        {evt.user_role || "—"}
                                    </Badge>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
